/**
 * ESG SaaS Platform - Water API Service
 *
 * API service for water management data (GRI 303).
 */

import { api } from './api';

// Types
export type WaterCategory = 'Withdrawal' | 'Discharge' | 'Consumption' | 'Recycled';
export type WaterSource = 'Municipal Water' | 'Groundwater' | 'Surface Water' | 'Seawater' | 'Rainwater' | 'Recycled/Reused' | 'Third-party Water' | 'Produced/Process Water';
export type WaterUnit = 'm³' | 'ML' | 'kL';

// GRI 303 Types
export type WaterQualityType = 'freshwater' | 'other_water';
export type DischargeDestination = 'surface_water' | 'groundwater' | 'seawater' | 'third_party' | 'on_site_treatment';
export type DischargeTreatmentLevel = 'none' | 'primary' | 'secondary' | 'tertiary';
export type ThirdPartyWaterType = 'municipal_supply' | 'wastewater_from_org';
export type IntensityMetricType = 'per_revenue' | 'per_employee' | 'per_area' | 'per_unit';

// CDP W1.2d: Water Recycling Types
export type RecyclingType = 'internal' | 'external';
export type ReusePurpose = 'cooling' | 'process' | 'irrigation' | 'sanitation' | 'other';

export interface WaterEntryCreate {
  category: WaterCategory;
  source?: WaterSource;  // Optional for Discharge category
  amount: number;
  unit?: WaterUnit;
  facility_id: string;
  year: number;
  month: number;
  reporting_period?: string;
  stressed_area?: boolean;
  quality_data?: Record<string, any>;
  notes?: string;

  // GRI 303: Water Quality Classification
  water_quality_type?: WaterQualityType;
  tds_mg_per_liter?: number;

  // GRI 303-4: Discharge-specific fields
  discharge_destination?: DischargeDestination;
  discharge_treatment_level?: DischargeTreatmentLevel;

  // CDP Water Security
  third_party_type?: ThirdPartyWaterType;
  is_produced_water?: boolean;

  // CDP W1.2d: Water Recycling fields (only for category="Recycled")
  recycling_type?: RecyclingType;
  reuse_purpose?: ReusePurpose;
  input_volume?: number;  // Volume going INTO treatment/recycling system (m³)
  output_volume?: number;  // Volume coming OUT (recycled water in m³)
}

export interface WaterEntry {
  id: string;
  category: string;
  source: string;
  amount: number;
  amount_m3: number;  // Normalized amount in cubic meters
  unit: string;
  facility_id: string;
  facility_name: string;
  year: number;
  month: number;
  reporting_period: string;
  stressed_area: boolean;
  status: string;
  created_by: string;
  created_at: string;
  approved_by?: string;
  approved_at?: string;

  // GRI 303 fields
  water_quality_type?: string;
  tds_mg_per_liter?: number;
  discharge_destination?: string;
  discharge_treatment_level?: string;

  // CDP Water Security fields
  third_party_type?: string;
  is_produced_water?: boolean;

  // CDP W1.2d: Water Recycling fields
  recycling_type?: string;
  reuse_purpose?: string;
  input_volume?: number;
  output_volume?: number;
  recycling_efficiency?: number;  // Auto-calculated: (output/input) * 100
}

export interface WaterListResponse {
  entries: WaterEntry[];
  total: number;
  limit: number;
  offset: number;
  total_withdrawal: number;
  total_discharge: number;
  total_recycled: number;
  approved_count: number;
  pending_count: number;
}

export interface WaterSummary {
  total_withdrawal: number;
  total_discharge: number;
  total_consumption: number;
  total_recycled: number;
  recycling_rate: number;
  by_source: Record<string, number>;
  by_facility: Record<string, number>;
  stressed_area_percentage: number;
  approved_count: number;
  pending_count: number;
  completion_percentage: number;
}

// GRI 303 Response Types
export interface SourceBreakdown {
  source: string;
  freshwater_volume: number;
  other_water_volume: number;
  stressed_area_volume: number;
  total_volume: number;
}

export interface DestinationBreakdown {
  destination: string;
  freshwater_volume: number;
  other_water_volume: number;
  stressed_area_volume: number;
  treatment_level?: string;
  total_volume: number;
}

export interface GRI303Summary {
  // GRI 303-3: Water Withdrawal
  total_withdrawal: number;
  withdrawal_freshwater_total: number;
  withdrawal_other_water_total: number;
  withdrawal_stressed_total: number;
  withdrawal_by_source: SourceBreakdown[];

  // GRI 303-4: Water Discharge
  total_discharge: number;
  discharge_freshwater_total: number;
  discharge_other_water_total: number;
  discharge_stressed_total: number;
  discharge_by_destination: DestinationBreakdown[];

  // GRI 303-5: Water Consumption
  total_consumption: number;
  consumption_stressed_total: number;
  consumption_change_percent?: number;

  // Metadata
  reporting_period?: string;
  generated_at: string;
}

// CDP Intensity Metrics Types
export interface IntensityMetric {
  metric_type: IntensityMetricType;
  intensity_value: number;
  numerator_unit: string;
  denominator_unit: string;
  denominator_value?: number;
}

export interface RecyclingTypeBreakdown {
  recycling_type: string;  // internal | external
  volume: number;
  percentage: number;
}

export interface ReusePurposeBreakdown {
  purpose: string;  // cooling | process | irrigation | sanitation | other
  volume: number;
  percentage: number;
}

export interface RecyclingMetrics {
  total_recycled: number;
  recycling_rate_percent: number;  // Single-pass rate, capped at 100%
  recycling_ratio_percent?: number;  // Multi-pass ratio, can exceed 100%
  comparison_to_baseline?: number;
  // CDP W1.2d breakdowns
  by_type: RecyclingTypeBreakdown[];
  by_purpose: ReusePurposeBreakdown[];
  // Treatment efficiency metrics
  total_input_volume: number;
  total_output_volume: number;
  average_efficiency_percent?: number;  // (total_output / total_input) * 100
}

export interface IntensityMetricsResponse {
  intensity_metrics: IntensityMetric[];
  recycling_metrics: RecyclingMetrics;
  total_withdrawal: number;
  reporting_period?: string;
  generated_at: string;
}

// Query parameters
interface ListParams {
  period?: string;
  facility?: string;
  category?: WaterCategory;
  status?: string;
  limit?: number;
  offset?: number;
}

// Service functions
export const waterService = {
  // CRUD Operations
  createEntry: (entry: WaterEntryCreate) =>
    api.post<WaterEntry>('/water/entries', entry),

  listEntries: (params: ListParams = {}) => {
    const query = new URLSearchParams();
    if (params.period) query.append('period', params.period);
    if (params.facility) query.append('facility', params.facility);
    if (params.category) query.append('category', params.category);
    if (params.status) query.append('status', params.status);
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.offset) query.append('offset', params.offset.toString());
    const queryString = query.toString();
    return api.get<WaterListResponse>(`/water/entries${queryString ? `?${queryString}` : ''}`);
  },

  getEntry: (entryId: string) =>
    api.get<WaterEntry>(`/water/entries/${entryId}`),

  updateEntry: (entryId: string, entry: WaterEntryCreate) =>
    api.put<{ id: string; updated: boolean }>(`/water/entries/${entryId}`, entry),

  deleteEntry: (entryId: string) =>
    api.delete<{ id: string; deleted: boolean }>(`/water/entries/${entryId}`),

  // Approval workflow
  approveEntry: (entryId: string, comment?: string) =>
    api.post<{ id: string; status: string }>(`/water/entries/${entryId}/approve`, { comment }),

  rejectEntry: (entryId: string, reason: string) =>
    api.post<{ id: string; status: string; reason: string }>(`/water/entries/${entryId}/reject`, { reason }),

  // Bulk upload
  bulkUpload: async (file: File, period: string) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.postFormData(`/water/bulk-upload?period=${encodeURIComponent(period)}`, formData);
  },

  // Analytics
  getSummary: (period?: string, facilityId?: string) => {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    if (facilityId) params.append('facility_id', facilityId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return api.get<WaterSummary>(`/water/summary${query}`);
  },

  getByFacility: (period?: string) => {
    const query = period ? `?period=${period}` : '';
    return api.get<{ facilities: { facility_id: string; name: string; withdrawal: number; recycled: number; discharge: number }[] }>(
      `/water/by-facility${query}`
    );
  },

  getBySource: (period?: string) => {
    const query = period ? `?period=${period}` : '';
    return api.get<{ sources: { source: string; amount: number; percentage: number }[] }>(
      `/water/by-source${query}`
    );
  },

  getTrends: (startPeriod: string, endPeriod: string) =>
    api.get<{ periods: string[]; data: { period: string; withdrawal: number; recycled: number; recycling_rate: number }[] }>(
      `/water/trends?start_period=${startPeriod}&end_period=${endPeriod}`
    ),

  // Export
  exportData: (format: string = 'csv', period?: string) => {
    const query = new URLSearchParams({ format });
    if (period) query.append('period', period);
    return api.get<{ status: string; format: string; download_url: string; record_count: number }>(`/water/export?${query.toString()}`);
  },

  // GRI 303 Disclosure
  getGRI303Summary: (period?: string) => {
    const query = period ? `?period=${period}` : '';
    return api.get<GRI303Summary>(`/water/gri-303-summary${query}`);
  },

  // CDP Water Security Intensity Metrics
  getIntensityMetrics: (period?: string) => {
    const query = period ? `?period=${period}` : '';
    return api.get<IntensityMetricsResponse>(`/water/intensity-metrics${query}`);
  },
};

// ================== WATER ATTACHMENT TYPES ==================

export interface WaterAttachment {
  id: string;
  original_filename: string;
  file_size_bytes: number;
  file_type: string;
  uploaded_by: string;
  uploaded_at: string;
  water_entry_id?: string;
}

export interface WaterAttachmentUploadResponse {
  attachments: WaterAttachment[];
  total_uploaded: number;
  total_size_bytes: number;
}

// ================== WATER ATTACHMENT SERVICE ==================
export const waterAttachmentService = {
  /**
   * Upload multiple files as evidence attachments for water entries.
   * Files are stored in audit-friendly folder structure by year/month.
   */
  uploadFiles: async (
    files: File[],
    year: number,
    month: number
  ): Promise<WaterAttachment[]> => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const response = await api.postFormData<WaterAttachmentUploadResponse>(
      `/water-attachments/upload?year=${year}&month=${month}`,
      formData
    );
    return response.attachments;
  },

  /**
   * Download a water attachment file.
   * Admin only.
   */
  downloadAttachment: async (attachmentId: string): Promise<{ url: string; filename: string }> => {
    const response = await api.getBlob(`/water-attachments/${attachmentId}/download`);
    const url = URL.createObjectURL(response.blob);
    return { url, filename: response.filename || 'download' };
  },

  /**
   * Delete a water attachment.
   * Before approval: owner can delete. After approval: admin only.
   */
  deleteAttachment: (attachmentId: string) =>
    api.delete<{ deleted: boolean; id: string }>(`/water-attachments/${attachmentId}`),

  /**
   * List all attachments for a specific water entry.
   * Admin only.
   */
  listEntryAttachments: (entryId: string) =>
    api.get<WaterAttachment[]>(`/water-attachments/entry/${entryId}`),

  /**
   * Link an uploaded attachment to a water entry.
   */
  linkAttachmentToEntry: (attachmentId: string, entryId: string) =>
    api.post<WaterAttachment>(`/water-attachments/${attachmentId}/link/${entryId}`, {}),

  /**
   * Export all water evidence files as a ZIP archive for audit purposes.
   * Admin only.
   */
  exportAuditEvidence: async (year: number, month?: number): Promise<void> => {
    const query = new URLSearchParams({ year: year.toString() });
    if (month) query.append('month', month.toString());

    const response = await api.getBlob(`/water-attachments/audit-export?${query.toString()}`);
    const url = URL.createObjectURL(response.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = response.filename || `water_evidence_export_${year}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /**
   * Export water data as CSV or Excel file (Admin only).
   */
  exportData: async (format: 'csv' | 'xlsx' = 'csv', period?: string): Promise<void> => {
    const query = new URLSearchParams({ format });
    if (period) query.append('period', period);

    const response = await api.getBlob(`/water/export?${query.toString()}`);
    const url = URL.createObjectURL(response.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = response.filename || `water_export.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
};
